import { useEffect, useRef, useState } from "react";
import { IconBell, IconHelp, IconSearch, IconSettings, IconSignOut } from "../lib/icons";
import { firstNameFromEmail } from "../lib/commandCenter";

/**
 * The workspace top bar: a search field that opens the command palette, the
 * notifications bell (with unread badge), and a profile chip whose menu holds
 * Settings, How-to, and Sign out — moved off the old top bar so the surface
 * stays clean. (The owner's Command Center is a left-sidebar page, not here.)
 */
export function TopBar({
  email,
  name: savedName,
  unread,
  onOpenSearch,
  onOpenNotifications,
  onSettings,
  onGuide,
  onSignOut,
}: {
  email?: string;
  /** Name the user set in Settings (auth user_metadata); wins over the
   *  email-derived fallback so the chip matches what they typed. */
  name?: string;
  unread: number;
  onOpenSearch: () => void;
  onOpenNotifications: () => void;
  onSettings: () => void;
  onGuide: () => void;
  onSignOut: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const name = (savedName ?? "").trim() || firstNameFromEmail(email);
  const initial = name.charAt(0).toUpperCase();

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const runFromMenu = (fn: () => void) => {
    setMenuOpen(false);
    fn();
  };

  return (
    <header className="cc-topbar">
      {/* The search field is a button that opens the ⌘K palette — a real search
          input would duplicate the palette's fuzzy find. */}
      <button
        type="button"
        className="cc-search"
        onClick={onOpenSearch}
        aria-label="Search content, accounts, and actions"
      >
        <span className="cc-search-ico">
          <IconSearch size={16} />
        </span>
        <span className="cc-search-text">Search content, accounts, etc.</span>
        <kbd aria-hidden="true">⌘K</kbd>
      </button>

      <div className="cc-topbar-spacer" />

      <div className="cc-topbar-actions">
        <button
          type="button"
          className="cc-icon-btn"
          onClick={onOpenNotifications}
          aria-label={unread > 0 ? `Notifications (${unread} unread)` : "Notifications"}
        >
          <IconBell size={18} />
          {unread > 0 && (
            <span className="notif-badge" aria-hidden="true">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>

        <div className="cc-profile" ref={profileRef}>
          <button
            type="button"
            className="cc-profile-btn"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span className="cc-avatar" aria-hidden="true">
              {initial}
            </span>
            <span className="cc-profile-name">{name}</span>
          </button>

          {menuOpen && (
            <div className="cc-menu" role="menu">
              <div className="cc-menu-head">
                <div className="cc-profile-name">{name}</div>
                {email && <div className="cc-menu-email">{email}</div>}
              </div>
              <button type="button" className="cc-menu-item" role="menuitem" onClick={() => runFromMenu(onSettings)}>
                <IconSettings size={16} /> Settings
              </button>
              <button type="button" className="cc-menu-item" role="menuitem" onClick={() => runFromMenu(onGuide)}>
                <IconHelp size={16} /> How-to
              </button>
              <button type="button" className="cc-menu-item danger" role="menuitem" onClick={() => runFromMenu(onSignOut)}>
                <IconSignOut size={16} /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
